import { useCallback, useMemo, useRef, useState } from 'react';
import EChart from './EChart';
import { EChartClickParams, EChartsInstance } from './echartsCore';
import { buildTimeSeriesOption, getAxisExtent } from './timeSeriesOption';
import { useTimeRangeBrush } from './useTimeRangeBrush';
import { EChartThreshold, EChartTimeSeries, EChartYAxis } from './types';
import { ChartPlotLine } from '../../../types/ChartPlotLine';
import { useTimeRange } from '../../../context/TimeRangeContext';

export interface TimeSeriesChartProps {
    series: EChartTimeSeries[];
    tooltipType: number;
    plotLines?: ChartPlotLine[];
    legend?: boolean;
    height?: number;
    yAxis?: EChartYAxis[];
    thresholds?: EChartThreshold[];
    brush?: boolean;
    xExtent?: { min: number; max: number } | 'data';
    reverseStacks?: boolean;
    tooltipFormatter?: (params: any[]) => string;
    onSeriesClick?: (seriesName: string) => void;
    className?: string;
}

const EMPTY_PLOT_LINES: ChartPlotLine[] = [];

function TimeSeriesChart(props: TimeSeriesChartProps) {
    const [chart, setChart] = useState<EChartsInstance | null>(null);
    const timeRangeContext = useTimeRange();
    const timeRange = timeRangeContext.timeRange;
    const plotLines = props.plotLines ?? EMPTY_PLOT_LINES;

    const barBandFraction = useMemo(() => {
        const { min, max, stepMs } = getAxisExtent(timeRangeContext);
        if (min === undefined || max === undefined || !(max > min) || !stepMs) {
            return undefined;
        }
        return stepMs / (max - min);
    }, [timeRange.from?.valueOf(), timeRange.to?.valueOf(), timeRange.interval]);

    const option = useMemo(() => buildTimeSeriesOption({
        series: props.series,
        timeRangeContext,
        tooltipType: props.tooltipType,
        plotLines,
        legend: props.legend,
        yAxis: props.yAxis,
        thresholds: props.thresholds,
        brush: props.brush,
        xExtent: props.xExtent,
        reverseStacks: props.reverseStacks,
        tooltipFormatter: props.tooltipFormatter,
    }), [
        props.series,
        props.tooltipType,
        props.legend,
        props.yAxis,
        props.thresholds,
        props.brush,
        props.xExtent,
        props.reverseStacks,
        props.tooltipFormatter,
        props.plotLines,
        timeRange.from?.valueOf(),
        timeRange.to?.valueOf(),
        timeRange.interval,
        timeRange.timezone,
    ]);

    useTimeRangeBrush(props.brush === false ? null : chart, timeRangeContext, option);

    const clickRef = useRef(props.onSeriesClick);
    clickRef.current = props.onSeriesClick;

    const onInit = useCallback((instance: EChartsInstance) => setChart(instance), []);

    const onClick = useCallback((params: EChartClickParams) => {
        if (clickRef.current && params.seriesName) {
            clickRef.current(params.seriesName);
        }
    }, []);

    return (
        <EChart option={option} height={props.height} className={props.className}
                plotLines={plotLines} onInit={onInit} onClick={onClick}
                barBandFraction={barBandFraction}/>
    );
}

export default TimeSeriesChart;
